import {
  Box,
  Button,
  ButtonGroup,
  Center,
  HStack,
  Spinner,
  Tag,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { Board, Column, FlushPanelContent, Panel } from '@gamma/ui/layout';
import {
  CrowdStrikeMark,
  ElasticMark,
  HttpMark,
  SplunkMark,
} from '@gamma/ui/logos';
import { RouterLink } from '@gamma/ui/navigation';
import {
  Alert,
  ConfirmationModal,
  DeleteModal,
  Drawer,
  ErrorBoundary,
} from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useApolloClient } from '@apollo/client';
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Column as TableColumn } from 'react-table';
import { v4 as uuidv4 } from 'uuid';
import { ROUTES } from '../../../shared/constants';
import { i18n } from '../../../shared/localization';
import { AuthContext } from '../../../shared/providers/AuthProvider';
import {
  ElasticsearchExporter,
  Exporter,
  HttpExporter,
  LogscaleExporter,
  SplunkHecLogsExporter,
  useDeleteExporter,
  useGetExporters,
  useGetExportersPublic,
} from '../queries';

import {
  ElasticSearchForm,
  HttpForm,
  HumioForm,
  SplunkHECForm,
} from '../components';
import { DEFAULT_VERIFY_CERTIFICATE } from './IntegrationsConstants';

interface AlertExportProps {
  locked?: boolean;
}

const {
  alert,
  alertExports,
  exporters: exporterNames,
  forms,
  instructions,
  modal,
  toast,
} = i18n.pages.alertExport;

const { deleteModal, confirmation } = modal;

const {
  deleteSuccessDesc,
  deleteSuccessTitle,
  deleteFailDesc,
  deleteFailTitle,
} = toast;

const dataTableColumns: TableColumn<Exporter>[] = [
  {
    Header: i18n.tables.name,
    accessor: 'name',
  },
  {
    Header: i18n.tables.type,
    accessor: 'exporter',
    Cell: (props) => {
      switch (props.value) {
        case 'elasticsearch':
          return (
            <HStack>
              <ElasticMark boxSize={5} />
              <Text>{exporterNames.elastic}</Text>
            </HStack>
          );
        case 'http':
          return (
            <HStack>
              <HttpMark boxSize={5} />
              <Text>{`${exporterNames.http} ${exporterNames.exporter}`}</Text>
            </HStack>
          );
        case 'logscale':
          return (
            <HStack>
              <CrowdStrikeMark color="#FC0000" boxSize={5} />
              <Text>{exporterNames.logscale}</Text>
            </HStack>
          );
        case 'splunk_hec_logs':
          return (
            <HStack>
              <SplunkMark boxSize={5} />
              <Text>{exporterNames.splunk}</Text>
            </HStack>
          );
        default:
          return props.value;
      }
    },
  },
  {
    Header: i18n.tables.status,
    accessor: 'enabled',
    Cell: (props) => (
      <Tag
        size="md"
        variant="solid"
        colorScheme={props?.value ? 'green' : 'gray'}
      >
        {!props?.value ? i18n.tables.disabled : i18n.tables.enabled}
      </Tag>
    ),
  },
];

export const IntegrationsAlertExports = (_props: AlertExportProps) => {
  const navigate = useNavigate();
  const { exporter_id } = useParams();

  const showToastMessage = useToast();

  const { user, userRole } = useContext(AuthContext);
  const tenant = user?.attributes?.['custom:tenant_id'];

  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const { onClose, onOpen, isOpen } = useDisclosure();
  const {
    onClose: onDeleteClose,
    onOpen: onDeleteOpen,
    isOpen: isDeleteOpen,
  } = useDisclosure();

  const [formRef, setFormRef] = useState<RefObject<HTMLFormElement> | null>(
    null,
  );

  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [isFormUnchanged, setIsFormUnchanged] = useState<boolean>(true);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(false);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [newInProgress, setNewInProgress] = useState<string | null>(null);
  const [exporters, setExporters] = useState<
    SplunkHecLogsExporter[] | ElasticsearchExporter[] | LogscaleExporter[] | []
  >([]);
  const [activeExporter, setActiveExporter] = useState<
    SplunkHecLogsExporter | ElasticsearchExporter | LogscaleExporter | null
  >(null);
  const [refetched, setRefetched] = useState(uuidv4());

  const client = useApolloClient();

  const getExportsQuery =
    userRole === 'admin' ? useGetExporters : useGetExportersPublic;

  const { data, error, loading, refetch } = getExportsQuery({
    fetchPolicy: 'no-cache', // Bypass cache completely to avoid any stale data
    variables: {},
  });

  const dataExporters = data?.getExporters;

  // Wrap refetch to evict cache before refetching
  const refetchExporters = useCallback(async () => {
    try {
      // Forcefully evict all getExporters queries from Apollo cache
      client.cache.evict({ fieldName: 'getExporters' });
      client.cache.gc();

      // Refetch with clean cache
      const result = await refetch();

      // force DataTable re-render
      setRefetched(uuidv4());

      return result;
    } catch (error) {
      showToastMessage({
        status: 'error',
        title: 'Refetch failed',
      });
      throw error;
    }
  }, [client.cache, refetch, showToastMessage]);

  const [deleteExporter, { loading: deleteLoading }] = useDeleteExporter({
    onCompleted: async () => {
      // Refetch to update the list and force table re-render
      await refetchExporters();

      setIsUpdating(false);
      setActiveExporter(null);
      showToastMessage({
        status: 'success',
        title: deleteSuccessTitle,
        description: deleteSuccessDesc,
        isClosable: true,
        position: 'bottom-right',
      });
      clearInProgress();
      navigate(ROUTES.systemSettingsIntegrationsAlertExports);
    },
    onError: () => {
      setIsUpdating(false);
      showToastMessage({
        duration: null,
        status: 'error',
        title: deleteFailTitle,
        description: deleteFailDesc,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const deleteAlertExport = async () => {
    setIsUpdating(true);
    if (tenant && activeExporter?.exporter_id) {
      deleteExporter({
        variables: {
          exporter_id: {
            tenant,
            exporter_id: activeExporter?.exporter_id,
          },
        },
      });
    }
  };

  const handleOnClose = () => {
    navigate(ROUTES.systemSettingsIntegrationsAlertExports);
    onClose();
  };

  const handleSetNewInProgress = useCallback(
    (type: string) => {
      if (type === 'SplunkHecLogsExporter') {
        const newSplunkExporter = {
          name: 'New',
          exporter_id: '',
          tenant: '',
          exporter: '',
          enabled: true,
          hec_url: '',
          token: '',
          index: '',
          inProgress: true,
          tls: {
            verify_certificate: DEFAULT_VERIFY_CERTIFICATE,
          },
          __typename: 'SplunkHecLogsExporter',
        };
        setNewInProgress(type);
        setActiveExporter(newSplunkExporter);
        if (dataExporters) {
          setExporters([...dataExporters, newSplunkExporter]);
        } else {
          setExporters([newSplunkExporter]);
        }
      }

      if (type === 'ElasticsearchExporter') {
        const newElasticExporter: ElasticsearchExporter = {
          name: 'New',
          exporter_id: '',
          tenant: '',
          exporter: '',
          enabled: true,
          auth: {
            user: '',
            password: '',
          },
          bulk: {
            index: '',
          },
          endpoint: '',
          tls: {
            verify_certificate: DEFAULT_VERIFY_CERTIFICATE,
          },
          inProgress: true,
          __typename: 'ElasticsearchExporter',
        };
        setNewInProgress(type);
        setActiveExporter(newElasticExporter);
        if (dataExporters) {
          setExporters([...dataExporters, newElasticExporter]);
        } else {
          setExporters([newElasticExporter]);
        }
      }

      if (type === 'HttpExporter') {
        const newHttpExporter: HttpExporter = {
          name: 'New',
          exporter_id: '',
          tenant: '',
          exporter: '',
          enabled: true,
          http_auth: {
            basic: {
              username: '',
              password: '',
            },
            bearer: {
              token: '',
            },
            strategy: 'none',
          },
          headers: [],
          uri: '',
          tls: {
            verify_certificate: DEFAULT_VERIFY_CERTIFICATE,
          },
          inProgress: true,
          __typename: 'HttpExporter',
        };
        setNewInProgress(type);
        setActiveExporter(newHttpExporter);
        if (dataExporters) {
          setExporters([...dataExporters, newHttpExporter]);
        } else {
          setExporters([newHttpExporter]);
        }
      }

      if (type === 'LogscaleExporter') {
        const newLogscaleExporter: LogscaleExporter = {
          name: 'New',
          exporter_id: '',
          tenant: '',
          exporter: '',
          enabled: true,
          url: '',
          token: '',
          index: '',
          tls: {
            verify_certificate: DEFAULT_VERIFY_CERTIFICATE,
          },
          inProgress: true,
          __typename: 'LogscaleExporter',
        };
        setNewInProgress(type);
        setActiveExporter(newLogscaleExporter);
        if (dataExporters) {
          setExporters([...dataExporters, newLogscaleExporter]);
        } else {
          setExporters([newLogscaleExporter]);
        }
      }
    },
    [dataExporters],
  );

  const clearInProgress = useCallback(() => {
    setNewInProgress(null);
  }, []);

  const handleCancelInProgress = useCallback(() => {
    if (newInProgress) {
      setExporters(exporters.slice(1));
    }
    clearInProgress();
    navigate(ROUTES.systemSettingsIntegrationsAlertExports);
  }, [exporters, newInProgress, navigate, clearInProgress]);

  const handleSetExporter = useCallback(
    (exporter_id: string) => {
      const selectedExporter = dataExporters?.find(
        (exporter) => exporter.exporter_id === exporter_id,
      );
      if (selectedExporter) {
        setActiveExporter(selectedExporter);
      }
    },
    [dataExporters],
  );

  // Update activeExporter when dataExporters changes (e.g., after refetch)
  // This ensures the UI stays in sync with backend data
  useEffect(() => {
    if (exporter_id && dataExporters) {
      const updatedExporter = dataExporters.find(
        (exporter) => exporter.exporter_id === exporter_id,
      );
      if (updatedExporter) {
        setActiveExporter(updatedExporter);
      }
    }
  }, [dataExporters, exporter_id]);

  useEffect(() => {
    if (exporter_id) {
      handleSetExporter(exporter_id);
    } else if (params.get('new')) {
      const exporterType = params.get('new');
      exporterType && handleSetNewInProgress(exporterType);
    } else {
      setActiveExporter(null);
    }
    // Dependencies omitted intentionally:
    // - handleSetExporter: Including this would cause re-runs on every dataExporters update, which is unnecessary
    //   since this effect should only run when the user navigates (exporter_id or params change)
    // - handleSetNewInProgress: Same reasoning as handleSetExporter
  }, [exporter_id, params]);

  useEffect(() => {
    if (!params.get('new') && !newInProgress) {
      clearInProgress();
      if (dataExporters) {
        setExporters(dataExporters);
      } else {
        setExporters([]);
      }
      // Reset isUpdating when data refreshes after a save
      setIsUpdating(false);
    }
  }, [dataExporters, newInProgress, params, clearInProgress]);

  return (
    <ErrorBoundary page="AlertExport" styleClass="main">
      <>
        <Panel mb={4}>
          <VStack alignItems="start">
            <Text textStyle="h4">{alertExports}</Text>
            <Text mb={2} textStyle="body-md">
              {instructions.selectFrom}
            </Text>
            <Box mb={2}>
              <Alert variant="subtle">
                {instructions.configureOneOrMore}{' '}
                <Button
                  variant="link"
                  as={RouterLink}
                  target="_blank"
                  to="/docs/alerts/alert-export.html"
                >
                  {alert.link}
                </Button>
                .
              </Alert>
            </Box>
            <HStack spacing={4}>
              <Panel
                h="60px"
                w="200px"
                display="flex"
                alignItems="center"
                layerStyle="second"
                data-testid="humio-button"
                opacity={userRole === 'admin' ? 'initial' : '50%'}
                cursor={userRole === 'admin' ? 'pointer' : 'not-allowed'}
                _hover={{
                  backgroundColor: '#474747',
                  borderColor: 'border.layer.2',
                }}
                onClick={() =>
                  userRole === 'admin' &&
                  navigate({
                    pathname: ROUTES.systemSettingsIntegrationsAlertExports,
                    search: `new=LogscaleExporter`,
                  })
                }
              >
                <CrowdStrikeMark color="#FC0000" mr={2} />
                <Text fontWeight="500">
                  {exporterNames.crowdStrike}
                  <br />
                  {exporterNames.falconLogScale}
                </Text>
              </Panel>
              <Panel
                h="60px"
                w="200px"
                display="flex"
                alignItems="center"
                layerStyle="second"
                data-testid="elastic-search-button"
                opacity={userRole === 'admin' ? 'initial' : '50%'}
                cursor={userRole === 'admin' ? 'pointer' : 'not-allowed'}
                _hover={{
                  backgroundColor: '#474747',
                  borderColor: 'border.layer.2',
                }}
                onClick={() =>
                  userRole === 'admin' &&
                  navigate({
                    pathname: ROUTES.systemSettingsIntegrationsAlertExports,
                    search: `new=ElasticsearchExporter`,
                  })
                }
              >
                <ElasticMark mr={2} />
                <Text fontWeight="500">{exporterNames.elastic}</Text>
              </Panel>
              <Panel
                h="60px"
                w="200px"
                display="flex"
                alignItems="center"
                layerStyle="second"
                data-testid="http-button"
                opacity={userRole === 'admin' ? 'initial' : '50%'}
                cursor={userRole === 'admin' ? 'pointer' : 'not-allowed'}
                _hover={{
                  backgroundColor: '#474747',
                  borderColor: 'border.layer.2',
                }}
                onClick={() =>
                  userRole === 'admin' &&
                  navigate({
                    pathname: ROUTES.systemSettingsIntegrationsAlertExports,
                    search: `new=HttpExporter`,
                  })
                }
              >
                <HttpMark mr={2} />
                <Text fontWeight="500">{`${exporterNames.http} ${exporterNames.exporter}`}</Text>
              </Panel>
              <Panel
                h="60px"
                w="200px"
                display="flex"
                alignItems="center"
                layerStyle="second"
                data-testid="splunk-hec-button"
                opacity={userRole === 'admin' ? 'initial' : '50%'}
                cursor={userRole === 'admin' ? 'pointer' : 'not-allowed'}
                _hover={{
                  backgroundColor: '#474747',
                  borderColor: 'border.layer.2',
                }}
                onClick={() =>
                  userRole === 'admin' &&
                  navigate({
                    pathname: ROUTES.systemSettingsIntegrationsAlertExports,
                    search: `new=SplunkHecLogsExporter`,
                  })
                }
              >
                <SplunkMark mr={2} />
                <Text fontWeight="500">{exporterNames.splunk}</Text>
              </Panel>
            </HStack>
          </VStack>
        </Panel>
        <Board data-testid="alert-export">
          {loading || error ? (
            <Column>
              <Panel>
                <GraphQLReqStatus loading={loading} error={error} />
              </Panel>
            </Column>
          ) : (
            dataExporters && (
              <Column>
                <Panel>
                  <FlushPanelContent>
                    <DataTable
                      key={refetched}
                      data={dataExporters}
                      columns={dataTableColumns}
                      emptyBodyContent={
                        <Center m={4}>{i18n.tables.noResults}</Center>
                      }
                      onRowClick={
                        userRole !== 'admin'
                          ? undefined
                          : (row) => {
                              handleSetExporter(row.original.exporter_id);
                              navigate({
                                pathname: `${ROUTES.systemSettingsIntegrationsAlertExports}/${row.original.exporter_id}`,
                              });
                            }
                      }
                    />
                  </FlushPanelContent>
                </Panel>
              </Column>
            )
          )}
          <Drawer
            title={
              <>
                {activeExporter?.__typename === 'SplunkHecLogsExporter' && (
                  <HStack>
                    <SplunkMark />
                    <Text textStyle="h4">{`${exporterNames.splunk} ${exporterNames.exporter}`}</Text>
                  </HStack>
                )}
                {activeExporter?.__typename === 'LogscaleExporter' && (
                  <HStack>
                    <CrowdStrikeMark color="#FC0000" />
                    <Text textStyle="h4">{`${exporterNames.crowdStrike} ${exporterNames.exporter}`}</Text>
                  </HStack>
                )}
                {activeExporter?.__typename === 'ElasticsearchExporter' && (
                  <HStack>
                    <ElasticMark />
                    <Text textStyle="h4">{`${exporterNames.elastic} ${exporterNames.exporter}`}</Text>
                  </HStack>
                )}
                {activeExporter?.__typename === 'HttpExporter' && (
                  <HStack>
                    <HttpMark />
                    <Text textStyle="h4">{`${exporterNames.http} ${exporterNames.exporter}`}</Text>
                  </HStack>
                )}
              </>
            }
            size="lg"
            isOpen={userRole === 'admin' && !!activeExporter}
            isCloseDisabled={isCancelDisabled}
            closeOnOverlayClick={!isUpdating && !isCancelDisabled}
            onClose={() => {
              if (!isUpdating && !isCancelDisabled) {
                setIsUpdating(false);
                setActiveExporter(null);
                navigate(`${ROUTES.systemSettingsIntegrationsAlertExports}`);
              }
            }}
            body={
              <>
                {activeExporter?.__typename === 'SplunkHecLogsExporter' && (
                  <SplunkHECForm
                    isUpdating={isUpdating}
                    setFormRef={setFormRef}
                    onDeleteOpen={onDeleteOpen}
                    deleteLoading={deleteLoading}
                    setIsUpdating={setIsUpdating}
                    activeExporter={activeExporter}
                    clearInProgress={clearInProgress}
                    setIsSaveLoading={setIsSaveLoading}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsFormUnchanged={setIsFormUnchanged}
                    setIsCancelDisabled={setIsCancelDisabled}
                    refetchExporters={refetchExporters}
                  />
                )}
                {activeExporter?.__typename === 'LogscaleExporter' && (
                  <HumioForm
                    isUpdating={isUpdating}
                    setFormRef={setFormRef}
                    onDeleteOpen={onDeleteOpen}
                    deleteLoading={deleteLoading}
                    setIsUpdating={setIsUpdating}
                    clearInProgress={clearInProgress}
                    setIsSaveLoading={setIsSaveLoading}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsFormUnchanged={setIsFormUnchanged}
                    setIsCancelDisabled={setIsCancelDisabled}
                    activeExporter={activeExporter as LogscaleExporter}
                    refetchExporters={refetchExporters}
                  />
                )}
                {activeExporter?.__typename === 'ElasticsearchExporter' && (
                  <ElasticSearchForm
                    isUpdating={isUpdating}
                    setFormRef={setFormRef}
                    onDeleteOpen={onDeleteOpen}
                    deleteLoading={deleteLoading}
                    setIsUpdating={setIsUpdating}
                    clearInProgress={clearInProgress}
                    setIsSaveLoading={setIsSaveLoading}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsFormUnchanged={setIsFormUnchanged}
                    setIsCancelDisabled={setIsCancelDisabled}
                    activeExporter={activeExporter as ElasticsearchExporter}
                    refetchExporters={refetchExporters}
                  />
                )}
                {activeExporter?.__typename === 'HttpExporter' && (
                  <HttpForm
                    isUpdating={isUpdating}
                    setFormRef={setFormRef}
                    onDeleteOpen={onDeleteOpen}
                    deleteLoading={deleteLoading}
                    setIsUpdating={setIsUpdating}
                    clearInProgress={clearInProgress}
                    setIsSaveLoading={setIsSaveLoading}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsFormUnchanged={setIsFormUnchanged}
                    setIsCancelDisabled={setIsCancelDisabled}
                    activeExporter={activeExporter as HttpExporter}
                    refetchExporters={refetchExporters}
                  />
                )}
              </>
            }
            footer={
              <ButtonGroup w="100%" justifyContent="end">
                <Button
                  size="lg"
                  variant="solid"
                  colorScheme="gray"
                  isDisabled={isCancelDisabled}
                  data-testid="alert-exporter-cancel-button"
                  onClick={() => {
                    setIsUpdating(false);
                    setActiveExporter(null);
                    if (!isFormUnchanged) {
                      onOpen();
                    } else {
                      handleCancelInProgress();
                    }
                  }}
                >
                  {forms.cancel}
                </Button>
                <Button
                  size="lg"
                  variant="solid"
                  isDisabled={isSaveDisabled}
                  onClick={() => {
                    if (formRef?.current) {
                      formRef.current.dispatchEvent(
                        new Event('submit', {
                          bubbles: true,
                          cancelable: true,
                        }),
                      );
                    }
                  }}
                  data-testid="alert-exporter-save-button"
                  leftIcon={isSaveLoading ? <Spinner size={'xs'} /> : undefined}
                >
                  {forms.save}
                </Button>
              </ButtonGroup>
            }
          />
        </Board>
        <ConfirmationModal
          size="sm"
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={handleOnClose}
          body={confirmation.body}
          title={confirmation.title}
          onConfirm={() => handleCancelInProgress()}
        ></ConfirmationModal>
        <DeleteModal
          size="sm"
          isOpen={isDeleteOpen}
          onOpen={onDeleteOpen}
          onClose={() => {
            onDeleteClose();
            setIsUpdating(false);
          }}
          body={deleteModal.body}
          title={deleteModal.title}
          onConfirm={() => deleteAlertExport()}
        ></DeleteModal>
      </>
    </ErrorBoundary>
  );
};
